import { ThemeConfig } from 'antd';

const colors = {
  main: '#111111',
  bg: '#E0E0E0',
  accent: '#1890FF',
  accentBg: '#BADEFF',
  get textPrimary() {
    return this.main;
  },
  textSecondary: '#555555',
  textDisabled: '#767676',
  textPlaceHolder: '#D9D9D9',
  get textAnchor() {
    return this.accent;
  },
  textInvert: '#FFFFFF',
  textAlert: '#F43636',
  get objectPrimary() {
    return this.main;
  },
  get objectSecondary() {
    return this.textSecondary;
  },
  get objectDisabled() {
    return this.textDisabled;
  },
  stroke: '#ACACAC',
  get strokeButton() {
    return this.textPlaceHolder;
  },
  strokeHighContrast: '#3F3F3F',
  strokeDisabled: '#CCCCCC',
  get bgGrey() {
    return this.bg;
  },
  bgLightGray: '#F2F2F2',
  get bgModal() {
    return this.textInvert;
  },
  get bgSidebar() {
    return this.strokeHighContrast;
  },
  statusSuccess: '#00D930',
  statusSuccessBg: '#E6F5EC',
  get statusError() {
    return this.textAlert;
  },
  statusErrorBg: '#FFDEDE',
  statusInfo: '#40A9FF',
  statusInfoBg: '#F0F9FF',
  statusWarning: '#FAAD14',
  statusWarningBg: '#FFFBE6',
  statusWarningBorder: '#FFE58F',
  statusErrorBorder: '#FF8F8F',
  get otherDisabled() {
    return this.strokeDisabled;
  },
  otherOverlay: '#111111BF',
};

const theme: ThemeConfig = {
  token: {
    fontSize: 16,
    colorText: colors.main,
    colorPrimary: colors.main,
    colorBgLayout: colors.bg,
    colorError: colors.textAlert,
    colorLink: 'inherit',
    fontFamily: 'Noto Sans JP, sans-serif',
    controlHeight: 40,
    colorBorder: colors.strokeButton,
    borderRadius: 0,
    colorTextPlaceholder: colors.textPlaceHolder,
    colorTextDisabled: colors.textDisabled,
    colorTextSecondary: colors.textSecondary,
    fontWeightStrong: 700,
    colorTextDescription: colors.textSecondary,
  },
  components: {
    Layout: {
      siderBg: colors.bgSidebar,
      headerBg: colors.main,
      headerColor: colors.textInvert,
      headerPadding: '0 24px',
      headerHeight: 70,
    },
    Typography: {
      titleMarginBottom: 0,
      fontSizeHeading1: 24,
      fontSizeHeading2: 20,
      fontSizeHeading3: 16,
      colorSuccessText: colors.statusInfo,
    },
    Menu: {
      activeBarBorderWidth: 0,
      itemBg: 'transparent',
      padding: 0,
      paddingXS: 0,
      itemMarginBlock: 0,
      itemMarginInline: 0,
      groupTitleColor: 'inherit',
      fontSize: 14,
      groupTitleFontSize: 16,
      itemHeight: 44,
      itemSelectedColor: 'inherit',
      itemSelectedBg: 'transparent',
      itemColor: colors.textInvert,
      itemHoverColor: 'inherit',
    },
    Table: {
      fontSize: 14,
      cellPaddingBlock: 13,
      cellPaddingInline: 12,
      headerBg: colors.bgLightGray,
      borderColor: colors.strokeButton,
      rowHoverBg: colors.bg,
      rowSelectedBg: 'transparent',
      rowSelectedHoverBg: colors.bg,
    },
    Pagination: {
      fontSize: 14,
      itemSize: 32,
      borderRadius: 2,
      colorPrimary: colors.statusInfo,
      colorPrimaryHover: colors.statusInfo,
    },
    Button: {
      defaultBorderColor: colors.strokeButton,
      paddingInline: 16,
      fontSize: 14,
      fontSizeLG: 16,
      fontSizeSM: 12,
      controlHeightSM: 32,
      borderColorDisabled: 'transparent',
      borderRadius: 2,
      borderRadiusSM: 2,
      defaultShadow: '0 2px 0 0 #0000000a',
      primaryShadow: '0 2px 0 0 #0000000a',
      textHoverBg: 'transparent',
    },
    Badge: {
      statusSize: 6,
      colorInfo: colors.statusInfo,
      colorWarning: colors.statusWarning,
      colorSuccess: colors.statusSuccess,
      colorError: colors.statusError,
      colorTextPlaceholder: colors.textSecondary,
      fontSize: 14,
    },
    Modal: {
      titleFontSize: 16,
      titleLineHeight: 1.7,
      fontSize: 14,
      lineHeight: 1.7,
      colorText: colors.textSecondary,
      colorBgMask: colors.otherOverlay,
      boxShadow:
        '0px 9px 28px 8px #0000000D, 0px 6px 16px 0px #00000014, 0px 3px 6px -4px #0000001F',
      borderRadiusLG: 2,
      marginSM: 16,
      paddingMD: 24,
    },
    Card: {
      paddingLG: 32,
      bodyPaddingSM: 16,
      headerHeight: 54,
      headerPadding: 24,
      headerBg: colors.bgLightGray,
      colorBorderSecondary: colors.bg,
    },
    Tabs: {
      titleFontSize: 14,
      lineHeight: 1,
      itemSelectedColor: 'inherit',
      inkBarColor: colors.main,
      horizontalItemPadding: '13px 16px',
      horizontalItemGutter: 0,
      horizontalMargin: '0 0 32px 0',
      colorBorderSecondary: colors.stroke,
      lineWidthBold: 3,
      cardPadding: '13px 16px',
      cardBg: colors.bgLightGray,
    },
    Checkbox: {
      controlInteractiveSize: 16,
      borderRadiusSM: 2,
      colorPrimary: colors.accent,
      colorPrimaryHover: colors.accent,
      fontSize: 14,
    },
    Message: {
      contentPadding: '10px 16px',
      contentBg: colors.textInvert,
      borderRadiusLG: 2,
      boxShadow: '0px 2px 8px 0px #00000026',
      fontSize: 14,
    },
    Dropdown: {
      borderRadiusSM: 2,
      paddingBlock: 6,
      boxShadow:
        '0px 9px 28px 8px #0000000D, 0px 6px 16px 0px #00000014, 0px 3px 6px -4px #0000001F',
    },
    Divider: {
      marginLG: 12,
      colorSplit: colors.strokeButton,
    },
    Tag: {
      borderRadiusSM: 4,
      fontSizeSM: 10,
      lineHeightSM: 1.8,
      colorInfoBorder: colors.statusInfo,
      colorInfoBg: colors.statusInfoBg,
      colorInfo: colors.main,
      colorErrorBorder: colors.statusErrorBorder,
      colorErrorBg: colors.statusErrorBg,
      colorWarningBg: colors.statusWarningBg,
      colorWarningBorder: colors.statusWarningBorder,
    },
    Radio: {
      radioSize: 16,
      fontSize: 14,
      colorPrimary: colors.accent,
      colorBorder: colors.stroke,
      colorBgContainerDisabled: colors.bgLightGray,
    },
    Select: {
      optionSelectedBg: colors.bgLightGray,
      hoverBorderColor: colors.accent,
      activeBorderColor: colors.accent,
      activeOutlineColor: 'rgba(24, 144, 255, 0.2)',
    },
    Alert: {
      withDescriptionPadding: 12,
      colorErrorBorder: colors.textAlert,
      colorInfoBorder: colors.statusInfo,
      colorInfoBg: colors.statusInfoBg,
      colorInfo: colors.statusInfo,
      fontSizeLG: 14,
    },
    Empty: {
      fontSize: 14,
      margin: 8,
    },
    Collapse: {
      headerPadding: '12px 16px',
      contentPadding: '24px 32px',
      marginSM: 16,
      paddingXXS: 24,
    },
    Upload: {
      padding: 28,
      fontSize: 14,
      borderRadiusLG: 4,
      colorFillAlter: colors.bgLightGray,
    },
    Form: {
      colorWarning: colors.textAlert,
    },
    Input: {
      hoverBorderColor: colors.accent,
      activeBorderColor: colors.accent,
      activeShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },
    InputNumber: {
      colorWarning: colors.textAlert,
      colorWarningBorderHover: colors.textAlert,
      warningActiveShadow: '0 0 0 2px rgba(255,38,5,0.06)',
      hoverBorderColor: colors.accent,
      activeBorderColor: colors.accent,
      activeShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },
    DatePicker: {
      hoverBorderColor: colors.accent,
      activeBorderColor: colors.accent,
      activeShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
      colorPrimary: colors.accent,
      controlItemBgActive: '#e6f7ff',
      cellHoverBg: '#F3F3F3',
    },
    Slider: {
      railSize: 5,
      railBg: colors.bg,
      railHoverBg: colors.bg,
      trackBg: colors.accent,
      trackHoverBg: colors.accent,
      handleSize: 20,
    },
    Switch: {
      trackPadding: 1,
      trackHeight: 22,
      colorPrimary: colors.accent,
      colorPrimaryHover: colors.accent,
    },
    Tooltip: {
      colorBgSpotlight: colors.bgModal,
      colorTextLightSolid: colors.main,
      fontSize: 14,
      controlHeight: 0,
    },
  },
};

export { theme };
